export enum NpTestPlanStatus {
  NOT_STARTED = 'notStarted',
  DRAFT = 'draft',
  PENDING_LEAD_REVIEW = 'pendingLeadReview',
}

export enum NpTestPlanModality {
  VIRTUAL = 'virtual',
  IN_PERSON = 'inPerson',
}

/**
 * Narrows which test measures are grade appropriate. The set is fixed rather than
 * battery specific: what varies per battery is whether a grade is required at all.
 */
export enum NpTestPlanGradeLevel {
  PRE_K = 'preK',
  KINDERGARTEN = 'kindergarten',
  GRADE_1 = 'grade1',
  GRADE_2 = 'grade2',
  GRADE_3 = 'grade3',
  GRADE_4 = 'grade4',
  GRADE_5 = 'grade5',
  GRADE_6 = 'grade6',
  GRADE_7 = 'grade7',
  GRADE_8 = 'grade8',
  GRADE_9 = 'grade9',
  GRADE_10 = 'grade10',
  GRADE_11 = 'grade11',
  GRADE_12 = 'grade12',
  POST_SECONDARY = 'postSecondary',
}

export enum NpTestPlanMeasureType {
  RATING_SCALE = 'rating_scale',
  F2F = 'f2f',
}

export enum NpTestPlanInformant {
  SELF_REPORT = 'SelfReport',
  CAREGIVER = 'Caregiver',
  TEACHER = 'Teacher',
  THERAPIST = 'Therapist',
  OTHER = 'Other',
}

export interface NpTestPlanMeasure {
  id: string
  name: string
  measureType: NpTestPlanMeasureType
  estimatedTimeMin: number
  estimatedTimeMax: number
  informant: NpTestPlanInformant
  category: string
  sessionFormat: string
  minAge: number
  maxAge: number
}

export interface NpTestPlanMeasureScope {
  measureType: NpTestPlanMeasureType
  informant: NpTestPlanInformant
}

export interface NpTestPlanCondition {
  id: string
  name: string
  /**
   * True when the standard battery contains measures that are normed by school
   * grade, which is what puts the grade level field on the build form. Picking a grade stays
   * optional, and members of any age are asked — grade narrows the measures rather than gating
   * the build, so an adult in post-secondary education is as answerable as a child.
   * LyraTherapy does not expose this, so it is kept per condition.
   */
  usesGradeLevel: boolean
}

/**
 * The intake-note gate and the scheduled modality are not part of this payload: the entrypoint reads
 * the notes API, and `useNpTestPlanScheduledModality` the appointment linked to the intake note.
 */
export interface NpTestPlanSummary {
  episodeId: string
  patientId: string
  planId: string | null
  status: NpTestPlanStatus
  dueDate: string | null
  /** The modality the plan was built for, or null before a plan exists. */
  modality: NpTestPlanModality | null
  measures: NpTestPlanMeasure[]
  battery: { modality: NpTestPlanModality; conditionName: string } | null
}

export interface CreateNpTestPlanArgs {
  episodeId: string
  patientId: string
  modality: NpTestPlanModality
  conditionId: string
  /**
   * Derived from the member's date of birth and never shown to the provider. Null
   * only when the member has no date of birth on file.
   */
  patientAge: number | null
  /** Only sent for conditions whose battery requires it. */
  gradeLevel?: NpTestPlanGradeLevel
}
